package com.devi3east.bashbuddy.features.events.detail.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.Favorite
import androidx.compose.material.icons.filled.OpenInBrowser
import androidx.compose.material.icons.filled.Place
import androidx.compose.material.icons.filled.Schedule
import androidx.compose.material.icons.outlined.FavoriteBorder
import androidx.compose.material3.Button
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.FloatingActionButton
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.SnackbarResult
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateMapOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import coil3.compose.AsyncImage
import com.devi3east.bashbuddy.auth.domain.models.UserType
import com.devi3east.bashbuddy.common.util.FullScreenImageViewer
import com.devi3east.bashbuddy.dashboard.ui.components.dialogs.InfoAlertDialog
import com.devi3east.bashbuddy.dashboard.ui.components.dialogs.SignInAlertDialog
import com.devi3east.bashbuddy.features.app.data.AppState
import com.devi3east.bashbuddy.features.app.data.Screen
import com.devi3east.bashbuddy.features.common.util.DateTimeFormatUtil
import com.devi3east.bashbuddy.features.events.common.data.models.EventStatus
import com.devi3east.bashbuddy.features.events.common.data.models.Ticket
import com.devi3east.bashbuddy.features.events.detail.domain.PendingAddToCart
import com.devi3east.bashbuddy.features.events.detail.ui.components.EventDetailTicketCard
import com.devi3east.bashbuddy.features.events.detail.ui.components.JackpotCard
import com.devi3east.bashbuddy.footer.ui.components.FooterCard
import kotlinx.coroutines.Job
import kotlinx.coroutines.launch
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun EventDetailRoute(
    appState: AppState,
    eventId: String,
    slug: String
) {
    val viewModel: EventDetailViewModel = koinViewModel()
    val uiState by viewModel.uiState.collectAsState()

    /* ---------------------------------------------
       Load event by ID (slug is NOT data)
       --------------------------------------------- */
    LaunchedEffect(eventId) {
        viewModel.getEventDetail(eventId)
    }

    /* ---------------------------------------------
       Canonical slug enforcement (web-safe)
       --------------------------------------------- */
    LaunchedEffect(uiState.event) {
        val event = uiState.event ?: return@LaunchedEffect

        // If URL slug is wrong, replace it with canonical one
        if (event.slug.isNotBlank() && event.slug != slug) {
            appState.navController.navigate(
                Screen.EventDetailScreen.createRoute(
                    eventId = event._id,
                    slug = event.slug
                )
            ) {
                popUpTo(Screen.EventDetailScreen.route) {
                    inclusive = true
                }
                launchSingleTop = true
            }
        }
    }

    /* ---------------------------------------------
       UI (slug NOT passed down)
       --------------------------------------------- */
    EventDetailScreen(
        appState = appState,
        uiState = uiState,
        onAddToCart = { ticket, quantity, showSnackbar ->
            appState.cartViewModel.addToCart(ticket, quantity)
            showSnackbar("${ticket.title} added to cart", "View Cart")
        },
        onBack = {
            val popped = appState.navController.popBackStack()

            if (!popped) {
                // We were launched directly on EventDetail (web deep link)
                appState.navController.navigate(Screen.EventsScreen.route) {
                    popUpTo(0)
                    launchSingleTop = true
                }
            }
        },
        onNavigateToCart = {
            appState.navController.navigate(Screen.CartScreen.route)
        },
        onToggleFavorite = {
            val profile = appState.profile.value
            if (profile != null && profile.userType == UserType.USER) {
                viewModel.toggleFavorite()
            }
        },
        onNavigateToTerms = {
            appState.navController.navigate(Screen.TermsOfServiceScreen.route)
        },
        onNavigateToPrivacyPolicy = {
            appState.navController.navigate(Screen.PrivacyPolicyScreen.route)
        },
        onNavigateToContact = {
            appState.navController.navigate(Screen.ContactUsScreen.route)
        },
        onExternalPurchase = { url ->
            appState.urlOpener.openUrl(url)
        }
    )
}

@Composable
fun EventDetailScreen(
    appState: AppState,
    uiState: EventDetailUiState,
    onAddToCart: (ticket: Ticket, quantity: Int, showSnackbar: (String, String) -> Unit) -> Unit,
    onBack: () -> Unit,
    onNavigateToCart: () -> Unit,
    onToggleFavorite: () -> Unit,
    onNavigateToTerms: () -> Unit,
    onNavigateToPrivacyPolicy: () -> Unit,
    onNavigateToContact: () -> Unit,
    onExternalPurchase: (String) -> Unit
) {
    val profile by appState.profile.collectAsState()
    val currentProfile = profile
    var pendingAddToCart by remember { mutableStateOf<PendingAddToCart?>(null) }
    var showOrgRestrictionDialog by remember { mutableStateOf(false) }
    var showNoUserRestrictionDialog by remember { mutableStateOf(false) }
    var showAuthDialog by remember { mutableStateOf(false) }
    var selectedImageUrl by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(profile) {
        val pending = pendingAddToCart ?: return@LaunchedEffect

        // Prevent double execution
        pendingAddToCart = null

        // Ignore empty adds
        if (pending.quantity <= 0) return@LaunchedEffect

        when (profile?.userType) {
            UserType.USER -> {
                appState.cartViewModel.addToCart(
                    pending.ticket,
                    pending.quantity
                )
            }

            UserType.ORGANIZATION -> {
                showOrgRestrictionDialog = true
                showAuthDialog = false
            }

            else -> {
                // safety no-op (future roles)
            }
        }
    }


    val event = uiState.event
    val snackbarHostState = remember { SnackbarHostState() }
    val coroutineScope = rememberCoroutineScope()
    var snackbarJob by remember { mutableStateOf<Job?>(null) }
    val quantities = remember { mutableStateMapOf<String, Int>() }

    val formattedDate = event?.startInstant?.let { DateTimeFormatUtil.instantToDateString(it) } ?: ""
    val formattedTime = event?.startInstant?.let { DateTimeFormatUtil.instantToTimeString(it) } ?: ""

    BoxWithConstraints(modifier = Modifier.fillMaxSize()) {
        val maxWidthDp = maxWidth
        val targetWidth = when {
            maxWidthDp < 600.dp -> maxWidthDp
            maxWidthDp < 900.dp -> 600.dp
            else -> 720.dp
        }

        val contentModifier = Modifier
            .widthIn(max = targetWidth)
            .align(Alignment.TopCenter)

        Box(modifier = Modifier.fillMaxSize()) {
            when {
                uiState.isLoading -> {
                    Box(
                        modifier = Modifier
                            .fillMaxSize()
                            .background(MaterialTheme.colorScheme.background),
                        contentAlignment = Alignment.Center
                    ) {
                        CircularProgressIndicator()
                    }
                }

                uiState.errorMessage != null -> {
                    Box(
                        modifier = Modifier
                            .fillMaxSize()
                            .background(MaterialTheme.colorScheme.background),
                        contentAlignment = Alignment.Center
                    ) {
                        Text(
                            text = uiState.errorMessage,
                            color = MaterialTheme.colorScheme.error
                        )
                    }
                }

                event != null -> {
                    Column(
                        modifier = contentModifier
                            .padding(horizontal = 16.dp)
                            .verticalScroll(rememberScrollState())
                    ) {
                        // --- HERO IMAGE ---
                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .height(300.dp)
                        ) {
                            AsyncImage(
                                model = event.imageUrl,
                                contentDescription = "Event Image",
                                contentScale = ContentScale.Crop,
                                modifier = Modifier
                                    .matchParentSize()
                                    .clip(RoundedCornerShape(16.dp))
                                    .clickable { selectedImageUrl = event.imageUrl }
                            )

                            FloatingActionButton(
                                onClick = onBack,
                                containerColor = MaterialTheme.colorScheme.surface.copy(alpha = 0.85f),
                                modifier = Modifier
                                    .align(Alignment.TopStart)
                                    .padding(12.dp)
                                    .size(40.dp)
                            ) {
                                Icon(
                                    imageVector = Icons.AutoMirrored.Filled.ArrowBack,
                                    contentDescription = "Back",
                                    tint = MaterialTheme.colorScheme.primary
                                )
                            }

                            if (currentProfile != null && currentProfile.userType == UserType.USER) {
                                FloatingActionButton(
                                    onClick = onToggleFavorite,
                                    containerColor = MaterialTheme.colorScheme.surface.copy(alpha = 0.85f),
                                    modifier = Modifier
                                        .align(Alignment.BottomEnd)
                                        .padding(12.dp)
                                        .size(40.dp)
                                ) {
                                    Icon(
                                        imageVector = if (uiState.isFavorite) Icons.Filled.Favorite else Icons.Outlined.FavoriteBorder,
                                        contentDescription = if (uiState.isFavorite) "Unfavorite" else "Favorite",
                                        tint = if (uiState.isFavorite) Color.Red else MaterialTheme.colorScheme.primary
                                    )
                                }
                            }
                        }

                        Spacer(modifier = Modifier.height(16.dp))

                        // --- TITLE & HOST ---
                        Column(modifier = Modifier.fillMaxWidth()) {
                            Text(
                                text = event.title ?: "Draft Title",
                                textAlign = TextAlign.Center,
                                modifier = Modifier
                                    .align(Alignment.CenterHorizontally)
                                    .padding(horizontal = 30.dp),
                                style = MaterialTheme.typography.headlineMedium,
                                color = MaterialTheme.colorScheme.onBackground,
                            )

                            Text(
                                text = "Hosted by",
                                style = MaterialTheme.typography.bodySmall.copy(
                                    color = MaterialTheme.colorScheme.primary
                                )
                            )
                            Text(
                                text = event.createdByName,
                                textAlign = TextAlign.Center,
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .padding(top = 4.dp),
                                style = MaterialTheme.typography.bodyLarge.copy(
                                    color = MaterialTheme.colorScheme.primary
                                )
                            )
                        }

                        Spacer(modifier = Modifier.height(10.dp))

                        if (uiState.prizeList?.isNotEmpty() == true || event.fiftyFiftyJackpot > 0) {
                            JackpotCard(
                                amount = event.fiftyFiftyJackpot,
                                prizeList = uiState.prizeList,
                                modifier = Modifier.fillMaxWidth()
                            )
                        }

                        Spacer(modifier = Modifier.height(16.dp))

                        // --- DATE & TIME ---
                        Row(verticalAlignment = Alignment.CenterVertically) {
                            Icon(
                                imageVector = Icons.Default.Schedule,
                                contentDescription = null,
                                tint = MaterialTheme.colorScheme.primary,
                                modifier = Modifier.size(20.dp)
                            )
                            Spacer(modifier = Modifier.width(8.dp))
                            Text(
                                text = "$formattedDate • $formattedTime",
                                style = MaterialTheme.typography.bodyMedium
                            )
                        }

                        Spacer(modifier = Modifier.height(8.dp))

                        // --- LOCATION ---
                        Row(verticalAlignment = Alignment.CenterVertically) {
                            Icon(
                                imageVector = Icons.Default.Place,
                                contentDescription = null,
                                tint = MaterialTheme.colorScheme.primary,
                                modifier = Modifier.size(20.dp)
                            )
                            Spacer(modifier = Modifier.width(8.dp))
                            Text(
                                text = "${event.address?.city}, ${event.address?.state}, ${event.address?.zipCode}",
                                style = MaterialTheme.typography.bodyMedium
                            )
                        }

                        Spacer(modifier = Modifier.height(16.dp))

                        // --- DESCRIPTION ---
                        Text(
                            text = event.description ?: "No description provided.",
                            style = MaterialTheme.typography.bodyLarge,
                            lineHeight = 22.sp
                        )

                        Spacer(modifier = Modifier.height(16.dp))

                        when {
                            event.status == EventStatus.COMPLETED -> {
                                Box(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .padding(12.dp)
                                        .background(
                                            color = MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.6f),
                                            shape = RoundedCornerShape(12.dp)
                                        )
                                        .padding(16.dp),
                                    contentAlignment = Alignment.Center
                                ) {
                                    Column(horizontalAlignment = Alignment.CenterHorizontally) {
                                        Text(
                                            text = "Thanks for stopping by!",
                                            style = MaterialTheme.typography.bodyMedium.copy(
                                                fontWeight = FontWeight.Bold,
                                                color = MaterialTheme.colorScheme.primary
                                            )
                                        )
                                        Spacer(modifier = Modifier.height(6.dp))
                                        Text(
                                            text = "This event has officially ended, and tickets are no longer available. We appreciate your support and hope to see you at future events.",
                                            style = MaterialTheme.typography.bodySmall.copy(
                                                color = MaterialTheme.colorScheme.onSurfaceVariant
                                            ),
                                            textAlign = TextAlign.Center
                                        )
                                    }
                                }
                            }

                            uiState.showPurchaseButton && uiState.externalPurchaseUrl != null -> {
                                // External purchase
                                Button(
                                    onClick = { onExternalPurchase(uiState.externalPurchaseUrl) },
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .padding(horizontal = 12.dp),
                                    shape = RoundedCornerShape(12.dp)
                                ) {
                                    Icon(
                                        imageVector = Icons.Default.OpenInBrowser,
                                        contentDescription = null
                                    )
                                    Spacer(modifier = Modifier.width(8.dp))
                                    Text("Continue on Website")
                                }

                                Spacer(modifier = Modifier.height(8.dp))

                                Text(
                                    text = "Ticket sales are handled securely on the organizer’s event webpage.",
                                    style = MaterialTheme.typography.bodySmall,
                                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                                    textAlign = TextAlign.Center,
                                    modifier = Modifier.fillMaxWidth()
                                )
                            }

                            uiState.ticketList?.isNotEmpty() == true -> {
                                // In-app tickets
                                Text(text = "Available Tickets", style = MaterialTheme.typography.titleMedium)
                                Spacer(modifier = Modifier.height(8.dp))

                                uiState.ticketList.forEach { ticket ->
                                    val quantity = quantities[ticket._id] ?: 0

                                    EventDetailTicketCard(
                                        imageUrl = ticket.imageUrl.orEmpty(),
                                        ticketType = ticket.ticketType,
                                        title = ticket.title ?: "Ticket Title",
                                        serialNumber = ticket.serialNumber ?: "",
                                        donation = ticket.donation,
                                        availableQuantity = ticket.availableQuantity ?: 0,
                                        description = ticket.description.orEmpty(),
                                        quantity = quantity,
                                        onQuantityChange = { newQty -> quantities[ticket._id] = newQty },
                                        buttonText = if (currentProfile == null) "Sign In &\nAdd to Cart" else "Add to Cart",
                                        onAddToCart = {
                                            when {
                                                currentProfile == null -> {
                                                    pendingAddToCart = PendingAddToCart(ticket, quantity)
                                                    showAuthDialog = true
                                                }
                                                currentProfile.userType == UserType.ORGANIZATION -> showOrgRestrictionDialog = true
                                                currentProfile.userType == UserType.USER -> {
                                                    onAddToCart(ticket, quantity) { message, actionLabel ->
                                                        snackbarJob?.cancel()
                                                        snackbarJob = coroutineScope.launch {
                                                            val result = snackbarHostState.showSnackbar(
                                                                message = message,
                                                                actionLabel = actionLabel
                                                            )
                                                            if (result == SnackbarResult.ActionPerformed) {
                                                                onNavigateToCart()
                                                            }
                                                        }
                                                        quantities[ticket._id] = 0
                                                    }
                                                }
                                            }
                                        },
                                        onImageClick = { selectedImageUrl = ticket.imageUrl }
                                    )
                                }
                            }

                            else -> {
                                // Tickets unavailable
                                Text(
                                    text = "Tickets are currently unavailable.",
                                    style = MaterialTheme.typography.bodyMedium,
                                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                                    textAlign = TextAlign.Center,
                                    modifier = Modifier.fillMaxWidth()
                                )
                            }
                        }

                        Spacer(modifier = Modifier.height(24.dp))

                        // --- FOOTER DISCLAIMER ---
                        FooterCard(
                            onNavigateToTerms = { onNavigateToTerms() },
                            onNavigateToPrivacyPolicy = { onNavigateToPrivacyPolicy() },
                            onNavigateToContact = { onNavigateToContact() }
                        )

                        Spacer(modifier = Modifier.height(12.dp))
                    }
                }
            }

            // --- FULLSCREEN IMAGE VIEWER ---
            selectedImageUrl?.let { imageUrl ->
                FullScreenImageViewer(imageUrl = imageUrl, onDismiss = { selectedImageUrl = null })
            }

            // --- SIGN IN ALERT DIALOG ---
            if (showAuthDialog) {
                SignInAlertDialog(
                    authViewModel = appState.authViewModel,
                    onDismiss = { showAuthDialog = false },
                    onNavigateToSignUp = {
                        showAuthDialog = false
                        appState.navController.navigate(Screen.SignUpScreen.route)
                    },
                    onNavigateToCart = {
                        showAuthDialog = false
                        coroutineScope.launch {
                            kotlinx.coroutines.yield()
                            onNavigateToCart()
                        }
                    }
                )
            }

            // --- ALERT DIALOGS ---
            if (showNoUserRestrictionDialog) {
                InfoAlertDialog(
                    title = "User Account Required",
                    message = "Oops! You’re not logged in. You’ll need to sign up or sign in to grab tickets and join the fun!",
                    onDismiss = { showNoUserRestrictionDialog = false }
                )
            }

            if (showOrgRestrictionDialog) {
                InfoAlertDialog(
                    title = "Oops!",
                    message = "Looks like you're signed in with an Organization account. To grab tickets, switch over to a User account instead.",
                    onDismiss = { showOrgRestrictionDialog = false }
                )
            }
        }
    }
}